import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../contexts/ThemeContext';
import { useActions, usePatchAction } from '../../services/queries/useActions';
import { FilterChips } from './FilterChips';

const FILTERS = ['open', 'done', 'dismissed'] as const;
type Filter = (typeof FILTERS)[number];

const ACTION_TYPE_LABELS: Record<string, string> = {
  review_target: 'Target review',
  review_reminder: 'Reminder',
  review_stop: 'Stop-loss review',
  review_take: 'Take-profit review',
  review_thesis: 'Thesis review',
};

export function TodoList() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation<any>();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [filter, setFilter] = useState<Filter>('open');
  const { data, isLoading } = useActions(filter);
  const patch = usePatchAction();

  return (
    <View style={styles.container}>
      <FilterChips
        filters={FILTERS}
        value={filter}
        onChange={setFilter}
        labelPrefix="actions.filter"
      />

      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} style={{ marginTop: theme.spacing.xl }} />
      ) : (
        <FlatList
          data={data ?? []}
          keyExtractor={(item) => String(item.id)}
          ListEmptyComponent={<Text style={styles.empty}>{t('actions.empty')}</Text>}
          renderItem={({ item }) => (
            <View style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.title}>{item.title}</Text>
                <Text style={styles.meta}>
                  {t(`actions.type_${item.action_type}`, {
                    defaultValue: ACTION_TYPE_LABELS[item.action_type] ?? item.action_type,
                  })}
                  {item.symbol ? ` · ${item.symbol}` : ''}
                </Text>
                {!!item.detail && <Text style={styles.detail}>{item.detail}</Text>}
              </View>
              {filter === 'open' && (
                <View style={styles.rowActions}>
                  {item.prebuilt_prompt && (
                    <TouchableOpacity
                      onPress={() => {
                        navigation.navigate('Chat', {
                          screen: 'ChatScreen',
                          params: { initialMessage: item.prebuilt_prompt },
                        });
                      }}
                    >
                      <Text style={styles.action}>{t('actions.action')}</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity onPress={() => patch.mutate({ id: item.id, status: 'done' })}>
                    <Text style={styles.done}>{t('actions.markDone')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => patch.mutate({ id: item.id, status: 'dismissed' })}
                  >
                    <Text style={styles.dismiss}>{t('actions.dismiss')}</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        />
      )}
      <Text style={styles.disclaimer}>{t('common.advisoryDisclaimer')}</Text>
    </View>
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.background, padding: theme.spacing.lg },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.divider,
    },
    title: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    meta: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
      textTransform: 'uppercase',
    },
    detail: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeSm,
      marginTop: theme.spacing.xxs,
    },
    rowActions: { alignItems: 'flex-end', gap: theme.spacing.sm },
    done: {
      color: theme.colors.success,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    dismiss: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeSm },
    action: {
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    empty: { color: theme.colors.textTertiary, textAlign: 'center', marginTop: theme.spacing.xl },
    disclaimer: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
      marginTop: theme.spacing.md,
    },
  });
}
